import { supabase } from './supabase';

/**
 * Profile Picture Service
 * Handles profile picture uploads with review system
 * - First upload: Immediate approval
 * - Subsequent uploads: Require admin review
 */

export const profilePictureService = {
  /**
   * Upload a profile picture
   * @param {string} userId - User ID
   * @param {File} file - Image file to upload
   * @returns {Object} Upload result with status
   */
  async uploadProfilePicture(userId, file) {
    try {
      // Validate file
      if (!file) {
        throw new Error('No file provided');
      }

      // Check file type
      if (!file.type.startsWith('image/')) {
        throw new Error('File must be an image');
      }

      // Check file size (max 5MB)
      const maxSize = 5 * 1024 * 1024;
      if (file.size > maxSize) {
        throw new Error('Image must be less than 5MB');
      }
      
      // Get current profile to check if first upload
      const { data: profile, error: profileError } = await supabase
        .from('user_profiles')
        .select('profile_picture_change_count, profile_picture, profile_picture_status')
        .eq('id', userId)
        .single();

      if (profileError) {
        console.error('[Profile Picture Service] Error: Profile fetch failed');
        throw profileError;
      }

      const isFirstUpload = !profile.profile_picture_change_count || profile.profile_picture_change_count === 0;

      // Upload file to storage
      const fileExt = file.name.split('.').pop();
      const fileName = `${userId}-${Date.now()}.${fileExt}`;
      const filePath = fileName;

      const { data: uploadData, error: uploadError } = await supabase.storage
        .from('profile-pictures')
        .upload(filePath, file, {
          cacheControl: '3600',
          upsert: false
        });

      if (uploadError) {
        console.error('[Profile Picture Service] Error: Upload failed');
        throw uploadError;
      }

      // Get public URL
      const { data: urlData } = supabase.storage
        .from('profile-pictures')
        .getPublicUrl(filePath);

      const publicUrl = urlData.publicUrl;
      
      console.log('[Profile Picture Service] Generated public URL:', {
        filePath,
        publicUrl,
        url_length: publicUrl?.length
      });

      if (isFirstUpload) {
        // First upload - apply immediately without review
        const { error: updateError } = await supabase
          .from('user_profiles')
          .update({
            profile_picture: publicUrl,
            profile_picture_change_count: 1,
            profile_picture_status: 'approved',
            profile_picture_last_approved_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          })
          .eq('id', userId);

        if (updateError) {
          console.error('[Profile Picture Service] Error: Database update failed');
          throw updateError;
        }

        return { 
          success: true, 
          immediate: true, 
          url: publicUrl,
          message: 'Profile picture uploaded successfully'
        };
      } else {
        // Subsequent upload - submit for review
        const { error: reviewError } = await supabase
          .from('profile_picture_reviews')
          .insert({
            user_id: userId,
            previous_picture: profile.profile_picture,
            new_picture: publicUrl,
            status: 'pending'
          });

        if (reviewError) throw reviewError;

        // Update user profile with pending status
        const { error: updateError } = await supabase
          .from('user_profiles')
          .update({
            profile_picture_pending: publicUrl,
            profile_picture_status: 'pending',
            updated_at: new Date().toISOString()
          })
          .eq('id', userId);

        if (updateError) throw updateError;

        return { 
          success: true, 
          immediate: false, 
          url: publicUrl, 
          needsReview: true,
          message: 'Profile picture submitted for review'
        };
      }
    } catch (error) {
      console.error('[Profile Picture Service] Error: Upload failed -', error.message);
      return { 
        success: false, 
        error: error.message || 'Failed to upload profile picture'
      };
    }
  },

  /**
   * Get pending profile picture reviews for admin
   * @returns {Array} List of pending reviews
   */
  async getPendingReviews() {
    try {
      const { data, error } = await supabase
        .from('profile_picture_reviews')
        .select(`
          *,
          user:user_id(
            id,
            full_name,
            email,
            profile_picture
          )
        `)
        .eq('status', 'pending')
        .order('created_at', { ascending: false });

      if (error) throw error;
      return { success: true, data: data || [] };
    } catch (error) {
      console.error('Error fetching pending reviews:', error);
      return { success: false, error: error.message, data: [] };
    }
  },

  /**
   * Get user's review history
   * @param {string} userId - User ID
   * @returns {Array} User's review history
   */
  async getUserReviewHistory(userId) {
    try {
      const { data, error } = await supabase
        .from('profile_picture_reviews')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return { success: true, data: data || [] };
    } catch (error) {
      console.error('Error fetching review history:', error);
      return { success: false, error: error.message, data: [] };
    }
  },

  /**
   * Approve a profile picture
   * @param {string} reviewId - Review ID
   * @param {string} adminId - Admin user ID
   * @returns {Object} Approval result
   */
  async approveProfilePicture(reviewId, adminId) {
    try {
      console.log('[Profile Picture Service] Calling approve_profile_picture RPC with:', {
        review_id: reviewId,
        admin_id: adminId
      });

      // First get the user_id from the review for sending email
      const { data: reviewData, error: reviewError } = await supabase
        .from('profile_picture_reviews')
        .select('user_id')
        .eq('id', reviewId)
        .single();

      if (reviewError) {
        console.error('[Profile Picture Service] Error fetching review:', reviewError);
        throw reviewError;
      }

      const { data, error } = await supabase.rpc('approve_profile_picture', {
        review_id: reviewId,
        admin_id: adminId
      });

      if (error) {
        console.error('[Profile Picture Service] Error: RPC failed -', error.message);
        throw error;
      }

      // Send email notification via Edge Function
      try {
        const { data: emailResult, error: emailError } = await supabase.functions.invoke('send-profile-picture-notification', {
          body: {
            userId: reviewData.user_id,
            status: 'approved'
          }
        });

        if (emailError) {
          console.error('[Profile Picture Service] Error: Email notification failed');
          // Don't throw - email failure shouldn't block the approval
        }
      } catch (emailErr) {
        console.error('[Profile Picture Service] Error: Email notification failed -', emailErr.message);
        // Don't throw - email failure shouldn't block the approval
      }

      return { 
        success: true, 
        message: 'Profile picture approved successfully' 
      };
    } catch (error) {
      console.error('[Profile Picture Service] Error: Approve failed -', error.message);
      return { 
        success: false, 
        error: error.message || 'Failed to approve profile picture'
      };
    }
  },

  /**
   * Reject a profile picture
   * @param {string} reviewId - Review ID
   * @param {string} adminId - Admin user ID
   * @param {string} reason - Rejection reason
   * @returns {Object} Rejection result
   */
  async rejectProfilePicture(reviewId, adminId, reason) {
    try {
      if (!reason || !reason.trim()) {
        throw new Error('Rejection reason is required');
      }

      // First get the user_id from the review for sending email
      const { data: reviewData, error: reviewError } = await supabase
        .from('profile_picture_reviews')
        .select('user_id')
        .eq('id', reviewId)
        .single();

      if (reviewError) {
        console.error('[Profile Picture Service] Error: Could not fetch review');
        throw reviewError;
      }

      const { data, error } = await supabase.rpc('reject_profile_picture', {
        review_id: reviewId,
        admin_id: adminId,
        reason: reason
      });

      if (error) {
        console.error('[Profile Picture Service] Error: RPC failed -', error.message);
        throw error;
      }

      // Send email notification via Edge Function
      try {
        const { data: emailResult, error: emailError } = await supabase.functions.invoke('send-profile-picture-notification', {
          body: {
            userId: reviewData.user_id,
            status: 'rejected',
            rejectionReason: reason
          }
        });

        if (emailError) {
          console.error('[Profile Picture Service] Error: Email notification failed');
          // Don't throw - email failure shouldn't block the rejection
        }
      } catch (emailErr) {
        console.error('[Profile Picture Service] Error: Email notification failed -', emailErr.message);
        // Don't throw - email failure shouldn't block the rejection
      }

      return { 
        success: true, 
        message: 'Profile picture rejected' 
      };
    } catch (error) {
      console.error('[Profile Picture Service] Error: Reject failed -', error.message);
      return { 
        success: false, 
        error: error.message || 'Failed to reject profile picture'
      };
    }
  },

  /**
   * Get all review history (for admin)
   * @returns {Array} All review history with user details
   */
  async getReviewHistory() {
    try {
      const { data, error } = await supabase
        .from('profile_picture_reviews')
        .select(`
          *,
          user:user_id(
            id,
            full_name,
            email,
            profile_picture
          )
        `)
        .in('status', ['approved', 'rejected'])
        .order('created_at', { ascending: false });

      if (error) throw error;
      return { success: true, data: data || [] };
    } catch (error) {
      console.error('Error fetching review history:', error);
      return { success: false, error: error.message, data: [] };
    }
  },

  /**
   * Delete old profile picture from storage
   * @param {string} pictureUrl - URL of picture to delete
   * @returns {Object} Deletion result
   */
  async deleteProfilePicture(pictureUrl) {
    try {
      if (!pictureUrl) return { success: true };

      // Extract file path from URL
      const url = new URL(pictureUrl);
      const pathMatch = url.pathname.match(/profile-pictures\/(.+)$/);
      
      if (!pathMatch) {
        console.warn('Could not extract file path from URL:', pictureUrl);
        return { success: true };
      }

      const filePath = pathMatch[1];

      const { error } = await supabase.storage
        .from('profile-pictures')
        .remove([filePath]);

      if (error) {
        console.warn('Error deleting old profile picture:', error);
        // Don't throw - this is a cleanup operation
      }

      return { success: true };
    } catch (error) {
      console.error('Error in deleteProfilePicture:', error);
      return { success: true }; // Return success anyway for cleanup
    }
  }
};

export default profilePictureService;
